import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import "../styles/navbar.modules.css";
import { usePages } from "../data/useLocations";

// ── POZYCJE MENU ─────────────────────────────────────────────────────────────

const MENU = [
  { to: "/lokalizacje", label: "Nasze lokalizacje", icon: "locs.png" },
  { to: "/kadra", label: "Kadra", icon: "team.png" },
  { to: "/urodziny", label: "Urodziny", icon: "birthday.png" },
  { to: "/wydarzenia", label: "Wydarzenia", icon: "event.png" },
  { to: "/kontakt", label: "Kontakt", icon: "information.png" },
];

const SYSTEM_URL = "https://moditsystem.pl";

// ── NAVBAR ───────────────────────────────────────────────────────────────────

function Navbar() {
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const { pages, loading: pagesLoading } = usePages();

  const [scrolled, setScrolled] = useState(false);
  const [popup, setPopup] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  /**
   * Wybrana lokalizacja wynika z adresu — nie trzymamy jej w osobnym stanie.
   * Wcześniej useEffect nie miał `locations` w zależnościach, więc nazwa nie
   * pojawiała się w przycisku, jeśli dane doszły po pierwszym renderze.
   */
  const selected = useMemo(() => {
    const slug = routerLocation.pathname.replace(/^\/+|\/+$/g, "");
    if (!slug) return null;
    return pages.find((p) => p.slug === slug) || null;
  }, [routerLocation.pathname, pages]);

  // ── SCROLL ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const el = document.getElementById("app-scroll");
    if (!el) return;

    const handleScroll = () => setScrolled(el.scrollTop > 10);

    handleScroll();
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // ── ZAMYKANIE MENU PRZY ZMIANIE STRONY ─────────────────────────────────────

  useEffect(() => {
    setPopup(false);
    setMobileOpen(false);
  }, [routerLocation.pathname]);

  // ── ESC ZAMYKA MODAL ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!popup) return;

    const onKey = (e) => {
      if (e.key === "Escape") setPopup(false);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [popup]);

  const handleSelect = useCallback(
    (loc) => {
      setPopup(false);
      navigate(`/${loc.slug}`);
    },
    [navigate]
  );

  const navBg = scrolled ? "#ededed" : "rgba(0,0,0,0.25)";
  const linkColor = scrolled ? "#333" : "#fff";

  const pickerButtonLabel = selected ? selected.name : "Wybierz lokalizację";

  return (
    <>
      {/* ── WERSJA NA KOMPUTER ── */}
      <nav
        className="pc_nav"
        style={{
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem 2rem",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 5,
          width: "100%",
          height: "60px",
          transition: "0.4s",
          backgroundColor: navBg,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "95%",
            height: "100%",
          }}
        >
          <ul
            style={{
              listStyle: "none",
              display: "flex",
              gap: "50px",
              margin: 0,
              padding: 0,
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Link to="/" aria-label="Strona główna 4STARS">
              <img
                src={scrolled ? "/logo_fullname_black.png" : "/logo_fullname_white.png"}
                style={{ width: "170px", height: "auto", display: "block" }}
                alt="4STARS Dance & Aerial Studio"
              />
            </Link>

            {MENU.map((m) => (
              <Link key={m.to} to={m.to} style={{ textDecoration: "none" }}>
                <li style={navLinkStyle(linkColor)}>{m.label}</li>
              </Link>
            ))}

            <a
              target="_blank"
              rel="noopener"
              href={SYSTEM_URL}
              style={{ textDecoration: "none" }}
            >
              <li style={navLinkStyle(linkColor)}>System</li>
            </a>
          </ul>

          <PickerButton label={pickerButtonLabel} onClick={() => setPopup(true)} size="pc" />
        </div>
      </nav>

      {/* ── WERSJA MOBILNA ── */}
      <nav
        className="mobile_nav"
        style={{
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem 2rem",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 5,
          width: "100%",
          height: "60px",
          transition: "0.4s",
          backgroundColor: navBg,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
          }}
        >
          <ul
            style={{
              listStyle: "none",
              display: "flex",
              gap: "50px",
              margin: 0,
              padding: 0,
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Link to="/" aria-label="Strona główna 4STARS">
              <img
                src="/logoshort.png"
                style={{ width: "50px", height: "auto", display: "block" }}
                alt="4STARS"
              />
            </Link>
          </ul>

          <PickerButton label={pickerButtonLabel} onClick={() => setPopup(true)} size="mobile" />

          <button
            className="menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={mobileOpen}
          >
            <div className="menu-icon-bar" />
            <div className="menu-icon-bar" />
            <div className="menu-icon-bar" />
          </button>
        </div>

        {mobileOpen && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              position: "fixed",
              top: "60px",
              right: 0,
              left: 0,
              zIndex: 6,
              padding: "12px",
              backgroundColor: "#ededed",
            }}
          >
            {MENU.map((m) => (
              <Link
                key={m.to}
                to={m.to}
                onClick={() => setMobileOpen(false)}
                style={{ textDecoration: "none", color: "inherit", display: "block", width: "100%" }}
              >
                <div style={mobileRowStyle}>
                  <img src={`/${m.icon}`} style={{ width: "26px" }} width="26" height="26" alt="" />
                  <span style={mobileLabelStyle}>{m.label}</span>
                </div>
              </Link>
            ))}

            <a
              target="_blank"
              rel="noopener"
              href={SYSTEM_URL}
              onClick={() => setMobileOpen(false)}
              style={{ textDecoration: "none", color: "inherit", display: "block", width: "100%" }}
            >
              <div style={mobileRowStyle}>
                <img src="/login.png" style={{ width: "26px" }} width="26" height="26" alt="" />
                <span style={mobileLabelStyle}>Zaloguj się do systemu</span>
              </div>
            </a>
          </div>
        )}
      </nav>

      {/* ── MODAL WYBORU — jeden dla obu wersji ── */}
      {popup && (
        <LocationPicker
          pages={pages}
          loading={pagesLoading}
          selected={selected}
          onSelect={handleSelect}
          onClose={() => setPopup(false)}
        />
      )}
    </>
  );
}

// ── PRZYCISK WYBORU ──────────────────────────────────────────────────────────

function PickerButton({ label, onClick, size }) {
  const isPc = size === "pc";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      style={{
        border: "none",
        backgroundColor: "#d0142d",
        padding: isPc ? "6px 25px" : "4px 20px",
        borderRadius: "20px",
        color: "rgb(250,250,250)",
        fontWeight: "bold",
        cursor: "pointer",
        fontSize: isPc ? "13px" : "12px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        fontFamily: "inherit",
      }}
    >
      <img
        src="/pin.png"
        style={{ width: isPc ? "28px" : "24px" }}
        width={isPc ? 28 : 24}
        height={isPc ? 28 : 24}
        alt=""
      />
      <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
        {label}
      </span>
    </button>
  );
}

// ── MODAL ────────────────────────────────────────────────────────────────────

function LocationPicker({ pages, loading, selected, onSelect, onClose }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Wybierz lokalizację"
      onClick={onClose}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        flexDirection: "column",
        gap: "10px",
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(15,15,15,0.65)",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 3000,
        padding: "16px",
        backdropFilter: "blur(6px)",
        alignContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          gap: "16px",
          maxWidth: "640px",
          width: "100%",
          margin: "auto",
          backgroundColor: "rgba(255,255,255,0.98)",
          borderRadius: "18px",
          padding: "18px 18px 16px",
          boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* h2, nie h1 — strona ma już swój nagłówek główny */}
          <h2 style={{ fontSize: "22px", margin: 0, color: "rgb(30,30,30)", fontWeight: 900 }}>
            Wybierz lokalizację
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            style={{
              cursor: "pointer",
              padding: "6px 10px",
              borderRadius: "12px",
              fontWeight: 900,
              color: "rgba(0,0,0,0.65)",
              background: "rgba(0,0,0,0.06)",
              border: "none",
              fontSize: "14px",
              fontFamily: "inherit",
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ position: "relative", width: "100%" }}>
          <button
            type="button"
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            style={{
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: "14px",
              padding: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              backgroundColor: "rgba(250,250,250,0.95)",
              boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
              width: "100%",
              fontFamily: "inherit",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: "92%",
                fontWeight: 800,
                color: selected ? "rgba(0,0,0,0.88)" : "rgba(0,0,0,0.45)",
                fontSize: "14px",
              }}
            >
              {selected
                ? selected.name
                : loading
                ? "Wczytywanie lokalizacji..."
                : "Wybierz lokalizację..."}
            </span>
            <span
              style={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.25s",
                fontWeight: 900,
                color: "rgba(0,0,0,0.55)",
              }}
            >
              ▼
            </span>
          </button>

          {isOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                left: 0,
                width: "100%",
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: "14px",
                backgroundColor: "rgba(255,255,255,0.98)",
                zIndex: 999,
                maxHeight: "320px",
                overflowY: "auto",
                boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
              }}
            >
              {loading ? (
                <div style={emptyRowStyle}>Wczytywanie...</div>
              ) : pages.length === 0 ? (
                <div style={emptyRowStyle}>Brak lokalizacji w bazie</div>
              ) : (
                pages.map((loc, index) => (
                  <button
                    key={loc.slug}
                    type="button"
                    onClick={() => onSelect(loc)}
                    style={{
                      padding: "12px",
                      borderTop: "none",
                      borderRight: "none",
                      borderLeft: "none",
                      borderBottom:
                        index === pages.length - 1
                          ? "none"
                          : "1px solid rgba(0,0,0,0.06)",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                      width: "100%",
                      background: "transparent",
                      textAlign: "left",
                      fontFamily: "inherit",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 900,
                          color: "rgba(0,0,0,0.88)",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                        }}
                      >
                        {loc.name}
                      </span>
                      {loc.address ? (
                        <span
                          style={{
                            fontWeight: 800,
                            fontSize: "12px",
                            color: "rgba(0,0,0,0.45)",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {loc.address}
                        </span>
                      ) : null}
                    </span>

                    <span
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "999px",
                        background: loc.color || "#C2001C",
                        boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                        flex: "0 0 auto",
                      }}
                    />
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div style={{ fontSize: "12px", fontWeight: 800, color: "rgba(0,0,0,0.45)" }}>
            Wybór przeniesie Cię do strony danej placówki
          </div>
        </div>
      </div>
    </div>
  );
}

// ── STYLE POMOCNICZE ─────────────────────────────────────────────────────────

const navLinkStyle = (color) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "15px",
  color,
  transition: "color 0.3s",
  fontWeight: 500,
  display: "block",
});

const mobileRowStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "3px",
  marginLeft: "30px",
  cursor: "pointer",
  width: "100%",
};

const mobileLabelStyle = {
  display: "block",
  padding: "12px",
  borderRadius: "10px",
  textDecoration: "none",
  fontWeight: 600,
  color: "#333",
  width: "100%",
};

const emptyRowStyle = {
  padding: "12px",
  color: "rgba(0,0,0,0.55)",
  fontWeight: 800,
  fontSize: "14px",
};

export default Navbar;
